import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";

import type { Market } from "./api";
import { COPY, money } from "./format";
import { priceLogQuery, type LogsParams, type PriceLogRow } from "./logs";

/** How many of the newest prices the drawer draws and lists (v26: the last six). */
export const HISTORY_COUNT = 6;

/** The Price log read the drawer asks for one row (C1 v1.35, owner ruling 2026-10-05): the newest page of the SKU's committed
 * prices in its market. The Logs tab asking the same thing shares the cache entry. */
export function historyParams(market: Market, sku: string): LogsParams {
  return {
    pane: "price", page: 0, market, sku, subject: "price", source: null, entryType: null, flag: null,
    product: null, variant: null, channel: null, store: null, location: null, marketplace: null, from: null, to: null, fulfilment: "current",
  };
}

/** The row's own newest prices, oldest first: the entries of its marketplace (or its store), newest first as the log serves
 * them, cut to `HISTORY_COUNT`. Matching is by the keys C2 wrote; no price is compared or computed. */
export function historyEntries(rows: PriceLogRow[], sku: string, place: string): PriceLogRow[] {
  return rows.filter((r) => r.subject_type === "price" && r.xero_sku === sku && r.marketplace_id === place).slice(0, HISTORY_COUNT).reverse();
}

/** The only numeric conversion in this file places a point on the line: a layout number, never a figure shown as fact (as Sparkline). */
const n = (value: string): number => Number(value);

/**
 * Under the drawer's Price history sentence: the last prices as a line, and the same prices listed (v26 `sparkline` and "Last N
 * prices"). One entry draws nothing, since the sentence above already says it. A failed read says so with Retry and never hides
 * the drawer; a pending one shows nothing yet.
 */
export function PriceHistory({ market, sku, place }: { market: Market; sku: string; place: string }) {
  const query = useQuery(priceLogQuery(historyParams(market, sku)));
  if (query.error && !query.data) {
    return (
      <p role="alert" className="mt-1 text-xs text-ks-grey-1" data-price-history="unread">
        {COPY.history.unread}<Button size="sm" variant="outline" className="ml-2" data-nonmutating data-retry onClick={() => void query.refetch()}>{COPY.retry}</Button>
      </p>
    );
  }
  const entries = query.data ? historyEntries(query.data.rows, sku, place) : [];
  if (entries.length < 2) return null;
  const W = 180, H = 40;
  const values = entries.map((e) => n(e.new_value));
  const min = Math.min(...values), span = (Math.max(...values) - min) || 1;
  const x = (i: number) => 4 + (i * (W - 8)) / (entries.length - 1);
  const y = (v: number) => H - 6 - ((v - min) / span) * (H - 12);
  const last = entries.length - 1;
  return (
    <div className="mt-2" data-price-history="read">
      <svg role="img" aria-label={COPY.history.chart(entries.length)} viewBox={`0 0 ${W} ${H}`} className="h-10 w-[180px]">
        <polyline points={values.map((v, i) => `${x(i)},${y(v)}`).join(" ")} fill="none" stroke="var(--color-ks-purple-2)" strokeWidth="1.6" />
        <circle cx={x(last)} cy={y(values[last])} r="3" fill="var(--color-ks-purple)" />
      </svg>
      <p className="text-xs text-ks-grey-1">{COPY.history.last(entries.length, entries.map((e) => money(e.new_value, e.currency)).join(", "))}</p>
    </div>
  );
}
